import axios from 'axios';
import { API_BASE_URL } from '@/config';

// 创建 axios 实例
const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000  // 增加到5分钟，历史数据查询可能需要更长时间
});

// 请求拦截器
service.interceptors.request.use(
  config => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('jwtToken');
    const role = localStorage.getItem('userRole');
    const userType = localStorage.getItem('userType');
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      
      // 解析JWT payload用于调试
      let jwtPayload = null;
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = parts[1];
          const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
          const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
          // 使用正确的方式解码UTF-8字符（支持中文）
          let decoded;
          try {
            decoded = decodeURIComponent(escape(atob(padded)));
          } catch (e) {
            decoded = atob(padded);
          }
          jwtPayload = JSON.parse(decoded);
        }
      } catch (e) {
        // 忽略解析错误
      }
      
      // 调试信息：确认JWT已添加到请求头
      if (config.url && config.url.includes('sensors')) {
        console.log('[请求] 传感器接口请求，已携带JWT:', {
          url: config.url,
          userType,
          role,
          hasToken: !!token,
          tokenPrefix: token.substring(0, 20) + '...',
          jwtPayload: jwtPayload ? {
            locationId: jwtPayload.locationId,
            locationName: jwtPayload.locationName,
            role: jwtPayload.role,
            sub: jwtPayload.sub,
            iat: jwtPayload.iat ? new Date(jwtPayload.iat * 1000).toLocaleString() : null,
            exp: jwtPayload.exp ? new Date(jwtPayload.exp * 1000).toLocaleString() : null,
            isExpired: jwtPayload.exp ? (Math.floor(Date.now() / 1000) >= jwtPayload.exp) : null
          } : null,
          localStorageRole: role
        });
      }
    } else {
      console.warn('[请求] 未找到JWT Token:', {
        url: config.url,
        userType,
        authToken: localStorage.getItem('authToken'),
        jwtToken: localStorage.getItem('jwtToken')
      });
    }
    
    if (role) {
      config.headers['X-User-Role'] = role;
    }
    
    // FormData 交给浏览器自动带 boundary，不要强行改成 JSON
    if (config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
      }
    } else if ((config.method === 'put' || config.method === 'post') && config.data) {
      // 确保Content-Type正确设置
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
      
      // 如果是对象，确保序列化为JSON字符串（axios会自动处理，但这里添加调试信息）
      if (typeof config.data === 'object' && !(config.data instanceof FormData)) {
        console.log('[请求拦截器] PUT/POST请求数据:', {
          url: config.url,
          method: config.method,
          dataType: typeof config.data,
          data: JSON.stringify(config.data),
          dataObject: config.data,
          headers: {
            'Content-Type': config.headers['Content-Type'],
            'Authorization': config.headers['Authorization'] ? 'Bearer ...' : '未设置'
          }
        });
      }
    }
    
    return config;
  },
  error => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response) {
      switch (error.response.status) {
        case 401: {
          // Token 过期或无效
          const errorMessage = error.response.data?.message || '认证失败';
          const token = localStorage.getItem('jwtToken') || localStorage.getItem('authToken');
          
          // 解析token用于调试
          let tokenInfo = null;
          if (token) {
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = parts[1];
                const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
                const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
                // 使用正确的方式解码UTF-8字符（支持中文）
                let decoded;
                try {
                  decoded = decodeURIComponent(escape(atob(padded)));
                } catch (e) {
                  decoded = atob(padded);
                }
                const jwtPayload = JSON.parse(decoded);
                const now = Math.floor(Date.now() / 1000);
                tokenInfo = {
                  locationId: jwtPayload.locationId,
                  locationName: jwtPayload.locationName,
                  role: jwtPayload.role,
                  sub: jwtPayload.sub,
                  iat: jwtPayload.iat ? new Date(jwtPayload.iat * 1000).toLocaleString() : null,
                  exp: jwtPayload.exp ? new Date(jwtPayload.exp * 1000).toLocaleString() : null,
                  isExpired: jwtPayload.exp ? (now >= jwtPayload.exp) : null,
                  remainingSeconds: jwtPayload.exp ? (jwtPayload.exp - now) : null
                };
              }
            } catch (e) {
              tokenInfo = { parseError: e.message };
            }
          }
          
          console.error('[响应拦截器] 401错误详情:', {
            url: error.config?.url,
            method: error.config?.method,
            message: errorMessage,
            responseData: error.response.data,
            requestHeaders: {
              authorization: error.config?.headers?.Authorization?.substring(0, 50) + '...',
              contentType: error.config?.headers?.['Content-Type']
            },
            tokenInfo: tokenInfo,
            localStorage: {
              hasToken: !!token,
              userType: localStorage.getItem('userType'),
              userRole: localStorage.getItem('userRole'),
              locationId: localStorage.getItem('locationId')
            }
          });

          // 对于 sensors 和 door-count 接口的 401 错误，不触发跳转（可能是权限问题，不影响其他功能）
          const requestUrl = error.config?.url || '';
          if (requestUrl.includes('sensors') || requestUrl.includes('door-count')) {
            console.warn('[响应拦截器] sensors/door-count 接口 401 错误，跳过自动跳转');
            break;
          }

          // 检查是否是"无效的令牌"错误，如果是，说明token本身有问题，需要重新登录
          if (errorMessage.includes('无效的令牌') || errorMessage.includes('无效') || errorMessage.includes('过期')) {
            console.error('[响应拦截器] Token无效，清除登录信息并跳转');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('userType');
            localStorage.removeItem('authToken');
            localStorage.removeItem('jwtToken');
            localStorage.removeItem('locationId');
            localStorage.removeItem('locationName');
            localStorage.removeItem('userRole');
            localStorage.removeItem('username');
            // 跳转到登录页
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          // 其他401错误（如权限不足）不自动跳转，由具体组件处理
          break;
        }
        case 403:
          console.error('没有权限访问');
          break;
        case 404:
          console.error('请求的资源不存在');
          break;
        case 500:
          console.error('服务器内部错误');
          break;
        default:
          console.error(`请求失败: ${error.response.status}`);
      }
    }
    return Promise.reject(error);
  }
);

export default service;

